import { ProductTypeEnum } from '@systemeio/digital-store-shared'
import { toast } from '@systemeio/ui'
import { BadRequest } from '@systemeio/util'
import { defaultDigitalProductErrors } from 'modules/digital-product/constants/default-values'
import { DigitalProductSourceEnum } from 'modules/digital-product/enums/digital-product-source-enum'
import { useDigitalProduct } from 'modules/digital-product/hooks/use-digital-product'
import { useDigitalProductActions } from 'modules/digital-product/hooks/use-digital-product-actions'
import { useOfferActions } from 'modules/digital-product/hooks/use-offer-actions'
import {
  DigitalProductChangeInterface,
  DigitalProductErrorInterface,
} from 'modules/digital-product/types/digital-product-interface'
import { getDigitalProductApiData } from 'modules/digital-product/utils/get-digital-product-api-data'
import {
  ProductDownloadItemErrorsType,
  ProductECourseItemErrorsType,
  ProductItemEditType,
} from 'modules/stores/store/content/product/types/product-interface'
import { useStore } from 'modules/stores/store/hooks/use-store'
import { useStoreQuery } from 'modules/stores/store/hooks/use-store-query'
import { StoreId } from 'modules/stores/types/stores-interface'
import { Dispatch, SetStateAction, useRef, useState } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { handle422Error } from 'shared/utils/parse-422-errors'
import { useErrorMessageInTabs as useProductDownloadErrorMessageInTabs } from '../product-download/hooks/use-error-message-in-tabs'
import { useSaveDownloadItem } from '../product-download/hooks/use-save-download-item'
import { useErrorMessageInTabs as useECourseErrorMessageInTabs } from '../product-e-course/hooks/use-error-message-in-tabs'
import { useSaveCourseItem } from '../product-e-course/hooks/use-save-course-item'

type ProductItemErrorsType = ProductDownloadItemErrorsType | ProductECourseItemErrorsType

type Props<StateType extends ProductItemEditType, ErrorsType extends ProductItemErrorsType> = {
  setErrors: Dispatch<SetStateAction<ErrorsType>>
  setProductErrors: Dispatch<SetStateAction<DigitalProductErrorInterface>>
  tempProduct: DigitalProductChangeInterface
  setTempProduct: Dispatch<SetStateAction<DigitalProductChangeInterface>>
  tempState: StateType
  setTempState: Dispatch<SetStateAction<StateType>>
  storeId: StoreId
  defaultErrors: ErrorsType
}

const DIGITAL_PRODUCT_SOURCE = DigitalProductSourceEnum.DigitalStore

export const useSaveItemProduct = <
  StateType extends ProductItemEditType,
  ErrorsType extends ProductItemErrorsType,
>({
  setErrors,
  setProductErrors,
  tempProduct,
  setTempProduct,
  tempState,
  setTempState,
  storeId,
  defaultErrors,
}: Props<StateType, ErrorsType>) => {
  const { t } = useLocoTranslation()
  const { data: store } = useStore(storeId)
  const { mutate: mutateDigitalProduct } = useDigitalProduct(
    tempState.checkoutPage.digitalProductId,
  )
  const { onEdit: onEditDigitalProduct, onCreate: onCreateDigitalProduct } =
    useDigitalProductActions()
  const { createOffer } = useOfferActions()
  const { redirectToMainPage } = useStoreQuery()
  const { formErrorMessage: formProductDownloadErrorMessageInTabs } =
    useProductDownloadErrorMessageInTabs()
  const { formErrorMessage: formECourseErrorMessageInTabs } = useECourseErrorMessageInTabs()
  const { saveItem: saveDownloadItem } = useSaveDownloadItem({ storeId })
  const { saveItem: saveECourseItem } = useSaveCourseItem({ storeId })

  const digitalProductId = useRef(tempState.checkoutPage.digitalProductId)
  const offerId = useRef(tempState.checkoutPage.offerId)
  const [isFetchingPublish, setIsFetchingPublish] = useState(false)
  const [isFetchingDraft, setIsFetchingDraft] = useState(false)

  const saveItemProduct = async (item: StateType) => {
    if (!digitalProductId.current) return
    if (!offerId.current) return

    if (item.productType === ProductTypeEnum.download) {
      await saveDownloadItem(item, digitalProductId.current, offerId.current)
    } else if (item.productType === ProductTypeEnum.eCourse) {
      await saveECourseItem(item, digitalProductId.current, offerId.current)
    }
  }

  const getErrorMessageInTabs = (
    item: StateType,
    errors: ErrorsType,
    digitalProductErrors: DigitalProductErrorInterface,
  ) => {
    if (item.productType === ProductTypeEnum.eCourse && 'courseId' in errors) {
      return formECourseErrorMessageInTabs({
        errors,
        digitalProductErrors,
      }).errorMessage
    } else if (item.productType === ProductTypeEnum.download) {
      return formProductDownloadErrorMessageInTabs({
        errors,
        digitalProductErrors,
      }).errorMessage
    }

    return null
  }

  const onSave = async (item: StateType) => {
    try {
      setErrors(defaultErrors)
      setProductErrors(defaultDigitalProductErrors)

      try {
        if (digitalProductId.current) {
          const responseData = await onEditDigitalProduct(
            digitalProductId.current,
            getDigitalProductApiData(tempProduct, DIGITAL_PRODUCT_SOURCE),
          )
          await mutateDigitalProduct(responseData, false)
        } else {
          const responseData = await onCreateDigitalProduct(
            getDigitalProductApiData(tempProduct, DIGITAL_PRODUCT_SOURCE),
          )
          digitalProductId.current = responseData.id
          setTempProduct(responseData)
          await mutateDigitalProduct(responseData, false)
        }
      } catch (e) {
        if (e instanceof BadRequest) {
          const typedError = e as BadRequest<DigitalProductErrorInterface>
          if (typedError.errors.fields) {
            const errors = typedError.errors.fields
            const digitalProductErrors = { ...defaultDigitalProductErrors, ...errors }

            setProductErrors(digitalProductErrors)

            const errorMessageInTabs = getErrorMessageInTabs(
              item,
              defaultErrors,
              digitalProductErrors,
            )
            errorMessageInTabs && toast.error(errorMessageInTabs)
            throw new Error()
          }
        }
      }

      if (!digitalProductId.current) {
        toast.error(t('global.error'))
        throw new Error()
      }

      if (!offerId.current) {
        const offer = await createOffer({ digitalProduct: digitalProductId.current })
        offerId.current = offer.id
      }

      if (!offerId.current) {
        toast.error(t('global.error'))
        throw new Error()
      }

      await saveItemProduct(item)

      toast.success(t('global.changes_saved'))
      await redirectToMainPage()
    } catch (e) {
      const newErrors = handle422Error<ErrorsType>(e, setErrors)

      const errorMessageInTabs = getErrorMessageInTabs(
        item,
        { ...defaultErrors, ...newErrors },
        defaultDigitalProductErrors,
      )
      errorMessageInTabs && toast.error(errorMessageInTabs)
    } finally {
      setIsFetchingPublish(false)
      setIsFetchingDraft(false)
    }
  }

  const onPublish = async () => {
    const item = { ...tempState, draft: false }
    setIsFetchingPublish(true)
    setTempState(item)
    await onSave(item)
  }

  const onSaveDraft = async () => {
    const item = { ...tempState, draft: true }
    setIsFetchingDraft(true)
    setTempState(item)
    await onSave(item)
  }

  return { onSaveDraft, onPublish, isFetchingPublish, isFetchingDraft }
}
